import { useEffect, useLayoutEffect, useState } from "react";

/**
 * Prüf-Chat CP-004 (02.11.2026, Nachtest) und Design-Prüfung (Inhaber 02.11.2026): Zwischen 768 und 1599 px lag die Erklärkarte
 * („Coco erklärt“) fest (position: fixed) oben rechts über dem Inhalt. Sie verdeckte bei 1024 × 768 Formulare (Eltern-Einwilligung,
 * Profil), und Klicks trafen die Karte. Das frühere Messen „verdeckt die Karte ein Feld?“ (Klasse tk-guide-neben) griff im
 * Creator-Portal nicht (der Inhalt lud erst nach der Messung, beim Scrollen wandert jedes Feld unter die feste Karte) und
 * quetschte im Agentur-Portal Listen auf 330 px Breite (K-566).
 *
 * Jetzt steht die Karte in diesem Bereich im Fluss der Seite: direkt unter dem Seitenkopf in main, über die volle Breite des
 * Inhalts. Sie verdeckt damit nie ein Feld oder einen Knopf und verengt keine Tabelle. Unter 768 px (Blatt über der unteren
 * Leiste) und ab 1600 px (eigene Spalte rechts, Platz am Inhalt reserviert) bleibt sie, wie sie war.
 */
function bereich(): MediaQueryList | null {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia("(min-width: 768px) and (max-width: 1599px)") : null;
}

/** Platz der Karte in main setzen bzw. halten: nach dem Seitenkopf, sonst nach der Überschrift, sonst ganz oben. */
function einsetzen(platz: HTMLElement): void {
  const main = [...document.querySelectorAll<HTMLElement>("main")].find((m) => !m.closest(".tk-guide-platz")) ?? null;
  if (!main) return;
  const kopf = main.querySelector<HTMLElement>(".pagehead") ?? main.querySelector<HTMLElement>(":scope > h1");
  if (kopf) {
    if (kopf.nextElementSibling !== platz) kopf.after(platz);
  } else if (main.firstElementChild !== platz) {
    main.prepend(platz);
  }
}

/**
 * Gibt zwischen 768 und 1599 px, solange die Karte offen ist, ihren Platz im Inhalt zurück (für createPortal), sonst null
 * (die Karte bleibt dann, wo sie gezeichnet wird). Der Platz folgt main, wenn die Seite neu zeichnet oder wechselt.
 */
export function useKartenPlatz(offen: boolean): HTMLElement | null {
  const [imFluss, setImFluss] = useState(() => !!bereich()?.matches);
  const [platz, setPlatz] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const mq = bereich();
    if (!mq) return;
    const neu = () => setImFluss(mq.matches);
    mq.addEventListener("change", neu);
    return () => mq.removeEventListener("change", neu);
  }, []);
  useLayoutEffect(() => {
    if (!offen || !imFluss || typeof document === "undefined") {
      setPlatz(null);
      return;
    }
    const el = document.createElement("div");
    el.className = "tk-guide-platz";
    einsetzen(el);
    // Seiten zeichnen main nach dem Laden neu (Ladezustand → Seite) oder wechseln es: der Platz zieht mit
    // (einsetzen ändert nur, wenn der Platz nicht stimmt; die eigene Änderung löst daher höchstens einen leeren Durchlauf aus)
    const beobachter = new MutationObserver(() => einsetzen(el));
    beobachter.observe(document.querySelector(".app") ?? document.body, { childList: true, subtree: true });
    setPlatz(el);
    return () => {
      beobachter.disconnect();
      el.remove();
      setPlatz(null);
    };
  }, [offen, imFluss]);
  return platz;
}
